import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send } from "lucide-react";
import { fmtDate } from "@/lib/constants";
import { toast } from "sonner";

export default function MessageThread({ clientId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/messages`);
      setMessages(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => { if (clientId) load(); /* eslint-disable-next-line */ }, [clientId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/messages`, { body: text });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card-surface rounded-2xl flex flex-col h-[60vh] md:h-[65vh]">
      <div className="flex-1 overflow-y-auto p-4 space-y-3 hide-scrollbar" data-testid="message-list">
        {messages.length === 0 && (
          <p className="text-slate-500 text-center text-sm mt-10">No messages yet. Start a secure conversation below.</p>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === user.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#C9A84A] text-[#0A0F1F]" : "bg-[#0A0F1F] border border-[#1E294B] text-white"}`}>
                {!mine && <div className="text-xs font-semibold opacity-70 mb-0.5">{m.sender_name} · {m.sender_role}</div>}
                <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                <div className={`text-[10px] mt-1 ${mine ? "text-[#0A0F1F]/60" : "text-slate-500"}`}>{fmtDate(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="border-t border-[#1E294B] p-3 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a secure message…"
          data-testid="message-input"
          className="flex-1 bg-[#0A0F1F] border border-[#1E294B] rounded-xl px-4 py-3 text-white outline-none focus:border-[#C9A84A]/60" />
        <button disabled={busy} data-testid="message-send"
          className="bg-[#C9A84A] hover:bg-[#E5C158] text-[#0A0F1F] rounded-xl px-4 flex items-center justify-center disabled:opacity-60">
          <Send size={18} />
        </button>
      </form>
    </div>
  );
}
